import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import cx from "classnames";
import styles from "../scss/DapRecommendedCover.module.scss";
import Icon from "../../common/components/Icon/Icon";
import Button from "../../common/components/Forms/Button/Button";
import InterimRecommendCoverItem from "./InterimRecommendCoverItem";
import { IInterimRecommendedCoverResults } from "../interfaces/IDigitalAdviceData";
import { AdviceControllerContext } from "../providers/DigitalAdviceProvider";
import DapQestionPlaceholder from "./Placeholder/DapQuestionPlaceholder";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import {
    generateQuoteGroup,
    getRecommendedCoverChangeFrequency
} from "../api/ApiDigitalAdvice";
import Loader from "../../common/components/Loader/Loader";
import { GenerateQuoteCategory } from "../enums/GenerateQuoteCategory";

interface IRecommendSection {
    recommendCover: IInterimRecommendedCoverResults;
    quoteGroupID?: string;
    next: (questionOrderBy: number) => Promise<void>;
}

const InterimRecommendedSection: React.FC<IRecommendSection> = (props) => {
    const history = useHistory();
    const { recommendCover } = props;
    const [isSubmit, setIsSubmit] = useState(false);
    const [frequency, setFrequency] = useState("Fortnight");
    const [frequencyLoading, setFrequencyLoading] = useState(false);
    const [coverAdviceAmount, setCoverAdviceAmount] = useState(0);
    const { selectors, actions } = useContext(AdviceControllerContext);

    const dropdownFrequencyTypes = [
        { value: "Yearly", label: "Yearly   " },
        { value: "Monthly", label: "Monthly   " },
        { value: "Fortnight", label: "Fortnight   " },
        { value: "Weekly", label: "Weekly   " }
    ];

    const handleComparePage = (): void => {
        setIsSubmit(true);
        generateQuoteGroup({
            sessionUserId: selectors.getSessionUserId(),
            visitorId: selectors.getVisitorId(),
            category: GenerateQuoteCategory.INTERIM
        })
            .then((quoteGroupID) => {
                setIsSubmit(false);
                history.push(`/results?groupId=${quoteGroupID}&da=interim-cover`);
            })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => {
                setIsSubmit(false);
            });
    };

    const handleContinueDAP = (): void => {
        setIsSubmit(true);

        props.next(0).finally(() => {
            setIsSubmit(false);
        });
    };

    const getAdviceWholeNumber = (amount): string => {
        if (!amount) return "$0";
        return (
            amount.search(/\./) &&
            amount.split(".") &&
            amount.split(".").length &&
            amount.split(".")[0]
        );
    };

    const getAdviceDecimalNumber = (amount): string => {
        if (!amount) return "00";
        return (
            (amount.search(/\./) &&
                amount.split(".") &&
                amount.split(".").length > 1 &&
                amount.split(".")[1]) ||
            "00"
        );
    };

    const handleCoverFrequency = (frequency): void => {
        setFrequency(frequency);
        setFrequencyLoading(true);
        const postData = {
            sessionUserId: selectors.getSessionUserId(),
            visitorId: selectors.getVisitorId(),
            isPrimaryAdult: true,
            recommendedCoverType: "Interim",
            frequency
        };

        const frequencyInfo = selectors.getFrequencyAdviceAmount(postData);
        if (frequencyInfo && frequencyInfo.adviceAmount) {
            setCoverAdviceAmount(frequencyInfo.adviceAmount);
            setFrequencyLoading(false);
            return;
        }
        getRecommendedCoverChangeFrequency(postData)
            .then((res) => {
                setCoverAdviceAmount(res?.adviceAmount);
                actions.setFrequencyAdviceAmount({
                    recommendedCoverType: "Interim",
                    frequency: frequency,
                    isPrimaryAdult: true,
                    adviceAmount: res.adviceAmount
                });
            })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => setFrequencyLoading(false));
    };

    useEffect(() => {
        if (
            recommendCover?.client &&
            recommendCover.client?.adviceAmount &&
            !coverAdviceAmount
        ) {
            setCoverAdviceAmount(recommendCover.client?.adviceAmount);
            actions.setFrequencyAdviceAmount({
                recommendedCoverType: "Interim",
                frequency: frequency,
                isPrimaryAdult: true,
                adviceAmount: recommendCover.client?.adviceAmount
            });
        }
    }, [recommendCover]);

    return (
        <React.Fragment>
            {!(recommendCover && recommendCover.client) && <DapQestionPlaceholder />}
            {recommendCover && recommendCover.client && (
                <div className={styles["recommend-section"]}>
                    <div className={styles["recommend-section--wrapper"]}>
                        <label className={styles["recommend-title"]}>
                            Here's what other people get
                        </label>
                        <p className={styles["recommend-sub-title"]}>
                            The world has become so fast paced that people don't want to
                            stand by reading a page of information, they would much
                            rather.
                        </p>
                        <div className={styles["recommend-container"]}>
                            <div className={styles["recommend-cover-section"]}>
                                <span className={styles["recommend-cover-price--from"]}>
                                    Total Premium From
                                </span>
                                <div
                                    className={
                                        styles["recommend-cover-price--container"]
                                    }>
                                    {frequencyLoading ? (
                                        <Loader
                                            className={styles["recommend-loader"]}
                                            icon="Default"
                                            size="default"
                                        />
                                    ) : (
                                        <div className={styles["recommend-cover-price"]}>
                                            <label
                                                className={
                                                    styles["recommend-cover-price--label"]
                                                }>
                                                {getAdviceWholeNumber(coverAdviceAmount)}
                                            </label>
                                            <div
                                                className={
                                                    styles["recommend-cover-price--extra"]
                                                }>
                                                <label
                                                    className={
                                                        styles[
                                                            "recommend-cover-price--extra-label"
                                                        ]
                                                    }>
                                                    .
                                                    {getAdviceDecimalNumber(
                                                        coverAdviceAmount
                                                    )}
                                                </label>
                                                <div
                                                    className={
                                                        styles[
                                                            "recommend-cover-price--extra-dropdown"
                                                        ]
                                                    }>
                                                    <span>/</span>
                                                    <Dropdown
                                                        id={`dropdown`}
                                                        name={`dropdown`}
                                                        options={dropdownFrequencyTypes}
                                                        selectedOptionId={frequency}
                                                        onChange={(e) => {
                                                            handleCoverFrequency(
                                                                e.target.value
                                                            );
                                                        }}
                                                        variant={"paymentfreq-full"}
                                                        className={
                                                            styles["recommend-type"]
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles["recommend-cover-container"]}>
                                <InterimRecommendCoverItem
                                    recommendCover={recommendCover.client}
                                    title={recommendCover?.includeAdult2 ? "For you" : ""}
                                />
                                {recommendCover &&
                                    recommendCover.includeAdult2 &&
                                    recommendCover.partner && (
                                        <InterimRecommendCoverItem
                                            recommendCover={recommendCover.partner}
                                            title={"For your partner"}
                                        />
                                    )}
                            </div>

                            <div className={styles["recommend-cover-actions"]}>
                                <Button
                                    className={cx("get-started")}
                                    width="content"
                                    component="button"
                                    size="m"
                                    onClick={handleComparePage}
                                    isSubmitting={isSubmit}
                                    isDisabled={isSubmit}>
                                    <span>Compare and apply now</span>
                                </Button>
                                <span className={styles["small-info-text"]}>
                                    Find an insurance provider
                                </span>
                            </div>
                        </div>

                        <div className={styles.buttonNext}>
                            <Button
                                className={cx("get-started")}
                                width="content"
                                component="button"
                                size="m"
                                onClick={handleContinueDAP}
                                isDisabled={isSubmit}
                                variant="calltoaction">
                                <span>Or, Continue</span>
                            </Button>
                            <span className={styles["small-info-text"]}>
                                Add more detail and get a more accurate recommendation
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default InterimRecommendedSection;
